import { useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FormFieldInput } from "@/components/FormFieldInput";
import { CONTACT_FIELDS, emptyValues, buildAnswers } from "@/lib/forms";
import { FaInstagram, FaTiktok, FaYoutube } from "react-icons/fa";
import { Mail, MapPin, Clock, Check } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [f, setF] = useState(emptyValues(CONTACT_FIELDS));
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    const missing = CONTACT_FIELDS.find((x) => x.required && !f[x.key]);
    if (missing) { toast.error(`Please fill in: ${missing.label}`); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) { toast.error("Please enter a valid email so I can reply to you."); return; }
    setSending(true);
    try {
      await api.post("/contact", { ...f, answers: buildAnswers(CONTACT_FIELDS, f) });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    setSending(false);
  };

  return (
    <div className="App bg-sugar-bg">
      <Navbar />
      <section className="sugar-hero-bg px-5 pb-8 pt-8">
        <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-sugar-ink2">Say hello</span>
        <h1 className="mt-3 font-display text-[2.4rem] font-semibold leading-tight text-sugar-ink">Contact <span className="italic font-normal text-sugar-rose">me</span></h1>
        <p className="mt-3 text-[15px] leading-relaxed text-sugar-ink2">Have a question, a custom idea, or just want to say hi? Send me a message and I'll get back to you as soon as I can. — Sarah</p>
      </section>

      <section className="px-5 py-6">
        <div className="grid gap-3">
          <a href="mailto:mysugarobsession@gmail.com" data-testid="contact-email" className="flex items-center gap-3 rounded-2xl border border-sugar-border bg-white p-4 hover:border-sugar-pink/60">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-sugar-blush text-sugar-pink"><Mail size={18} /></span>
            <div><div className="text-xs font-bold uppercase tracking-widest text-sugar-pink">Email</div><div className="font-semibold text-sugar-ink">mysugarobsession@gmail.com</div></div>
          </a>
          <div className="flex items-center gap-3 rounded-2xl border border-sugar-border bg-white p-4">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-sugar-blush text-sugar-pink"><MapPin size={18} /></span>
            <div><div className="text-xs font-bold uppercase tracking-widest text-sugar-pink">Serving</div><div className="font-semibold text-sugar-ink">Langley & Surrey, BC — free delivery</div></div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-sugar-border bg-white p-4">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-sugar-blush text-sugar-pink"><Clock size={18} /></span>
            <div><div className="text-xs font-bold uppercase tracking-widest text-sugar-pink">Pickup hours</div><div className="font-semibold text-sugar-ink">Daily, 9 AM – 8 PM (by arrangement)</div></div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center gap-6">
          <a href="https://www.instagram.com/mysugarobsession" target="_blank" rel="noreferrer" aria-label="Instagram" className="transition-transform hover:scale-110"><FaInstagram size={26} style={{ color: "#E1306C" }} /></a>
          <a href="https://www.youtube.com/@mysugarobsession" target="_blank" rel="noreferrer" aria-label="YouTube" className="transition-transform hover:scale-110"><FaYoutube size={28} style={{ color: "#FF0000" }} /></a>
          <a href="https://www.tiktok.com/@mysugarobsession" target="_blank" rel="noreferrer" aria-label="TikTok" className="transition-transform hover:scale-110"><FaTiktok size={23} style={{ color: "#010101" }} /></a>
        </div>
      </section>

      <section className="px-5 pb-10">
        <div className="rounded-3xl border border-sugar-border bg-white p-6 sugar-card-shadow">
          {done ? (
            <div className="py-6 text-center">
              <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-sugar-pink text-white"><Check size={26} /></motion.div>
              <h3 className="mt-4 font-display text-xl font-bold text-sugar-ink">Message sent!</h3>
              <p className="mt-2 text-sugar-ink2">Thanks for reaching out — I'll reply as soon as I can.</p>
            </div>
          ) : (
            <>
              <h2 className="font-display text-2xl font-bold text-sugar-ink">Send a message</h2>
              <div className="mt-5 space-y-4">
                {CONTACT_FIELDS.map((field) => (
                  <FormFieldInput key={field.key} field={field} value={f[field.key]} onChange={(v) => set(field.key, v)} />
                ))}
                <Button data-testid="contact-submit" disabled={sending} onClick={submit} className="w-full rounded-full bg-sugar-ink py-6 text-sm font-bold uppercase tracking-[0.18em] text-sugar-bg hover:bg-sugar-pinkhover">{sending ? "Sending…" : "Send message"}</Button>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
